import { TGetIndustryCurriculumListResDto } from "@/api/services/main/industry/dtos/get-industry-list.dto";
import { useMe } from "@/api/services/main/auth";
import { CACHED_TIME } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { toNumber } from "@/utils/number";

/**
 * 分野 (industry) curriculum list from the main (認証済み) service `/industries`. Unlike the global
 * `/global/industries` master list, this is scoped to the authenticated user and returns per-user
 * curriculum data + counts (subjectCount / mockExamQuestionCount) — the global endpoint returns
 * those unscoped/empty for RSO users. Used by the learner form/detail so 単語数・問題数 render
 * correctly. Named "IndustryCurriculum" to avoid clashing with global/master `useGetMainIndustryList`
 * (主要産業 master categories, a different concept).
 */
export const fetchIndustryCurriculumListApi = async (): Promise<TGetIndustryCurriculumListResDto> => {
  const { data } = await clientApi.get<TGetIndustryCurriculumListResDto>("/industries");
  return data;
};

export const useGetIndustryCurriculumList = () => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.MAIN.INDUSTRY_CURRICULUM_LIST],
    queryFn: () => fetchIndustryCurriculumListApi(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  // hiddenIndustryIds はログインユーザーの所属 (RSO / 企業 / 支援ユーザーは作成元RSO) を BE が
  // 解決して get-me が返す。company/RSO はそれぞれ独立した hiddenIndustryIds を持つ。
  const { data: meResult } = useMe();
  const hiddenIndustryIds = useMemo(
    () => new Set((meResult?.data?.hiddenIndustryIds ?? []).map(String)),
    [meResult],
  );

  const allIndustries = useMemo(() => data?.data ?? [], [data]);

  // Keep only published industries. Tolerant to `isPublished` being absent in the response
  // (treated as published) so the list isn't emptied if BE already filters server-side.
  const publishedIndustries = useMemo(
    () => allIndustries.filter((item) => item.isPublished !== false),
    [allIndustries],
  );

  /** Industries excluding those hidden by the user's RSO/company. */
  const visibleIndustries = useMemo(
    () => publishedIndustries.filter((item) => !hiddenIndustryIds.has(String(item.id))),
    [publishedIndustries, hiddenIndustryIds],
  );

  // 分野の科目数 (単語数・問題数の元) を返す。カリキュラムデータは main /industries のみが持つ。
  const getSubjectCountByIndustryId = useCallback(
    (industryId: number | string) => {
      const industry = publishedIndustries.find((item) => toNumber(item.id) === toNumber(industryId));
      return toNumber(industry?.subjectCount) ?? 0;
    },
    [publishedIndustries],
  );

  return { data: publishedIndustries, allIndustries, visibleIndustries, isLoading, getSubjectCountByIndustryId, ...rest };
};
